import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { FaEdit, FaTrash, FaPlus, FaFilePdf, FaFileExcel, FaTimes, FaMinus, FaInfoCircle } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ProductForm from './ProductForm';
import StockEntry from './StockEntry';
import StockExit from './StockExit';

const InventoryList = () => {
    const [produtos, setProdutos] = useState([]);
    const [produtosFiltrados, setProdutosFiltrados] = useState([]);
    const [produtoEditado, setProdutoEditado] = useState(null);
    const [mostrarModalEdicao, setMostrarModalEdicao] = useState(false);
    const [lotes, setLotes] = useState({});
    const [filtroProduto, setFiltroProduto] = useState('');
    const [estoqueCritico, setEstoqueCritico] = useState(0);
    const [exibirAlerta, setExibirAlerta] = useState(true);
    const [produtoMovimentacao, setProdutoMovimentacao] = useState(null);  
    const [tipoMovimentacao, setTipoMovimentacao] = useState(null);
    const [filtrarCriticos, setFiltrarCriticos] = useState(false);
    const [viewMode, setViewMode] = useState('table'); // ← ADICIONAR
    const [isMobile, setIsMobile] = useState(false); // ← ADICIONAR

    // Detectar mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        carregarProdutos();
        carregarLotes();
    }, []);

    useEffect(() => {
        let produtosFiltrados = produtos.filter(produto =>
            produto.nome.toLowerCase().includes(filtroProduto.toLowerCase())
        );

        if (filtrarCriticos) {
            produtosFiltrados = produtosFiltrados.filter(produto => {
                const estoqueAtual = Number(produto.estoque_atual);
                const estoqueMinimo = Number(produto.estoque_minimo);
                return estoqueAtual <= estoqueMinimo && !(estoqueMinimo === 1 && estoqueAtual === 1);
            });
        }

        setProdutosFiltrados(produtosFiltrados);
    }, [filtroProduto, produtos, filtrarCriticos]);

    const carregarProdutos = async () => {
        try {
            const response = await api.get('/produtos');
            const produtosOrdenados = response.data.sort((a, b) => a.nome.localeCompare(b.nome));
            setProdutos(produtosOrdenados);

            const produtosCriticos = response.data.filter(produto => {
                const estoqueAtual = Number(produto.estoque_atual);
                const estoqueMinimo = Number(produto.estoque_minimo);
                return estoqueAtual <= estoqueMinimo && !(estoqueMinimo === 1 && estoqueAtual === 1);
            });
            
            setEstoqueCritico(produtosCriticos.length);
            setExibirAlerta(produtosCriticos.length > 0);
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
        }
    };

    const carregarLotes = async () => {
        try {
            const response = await api.get('/lotes');
            const lotesPorProduto = {};
        
            response.data.forEach(lote => {
                if (!lotesPorProduto[lote.produto_id] || lote.id > lotesPorProduto[lote.produto_id].id) {
                    lotesPorProduto[lote.produto_id] = {
                        ...lote,
                        preco_custo: Number(lote.preco_custo) || 0
                    };
                }
            });
    
            setLotes(lotesPorProduto);
        } catch (error) {
            console.error("Erro ao buscar lotes:", error);
        }
    };

    const obterDataHoraAtual = () => {
        return new Date().toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).replace(/[/: ]/g, '_');
    };

    const exportarParaExcel = () => {
        const dataHora = obterDataHoraAtual();
        const worksheet = XLSX.utils.json_to_sheet(produtos.map(produto => {
            const lote = lotes[produto.id] || {};
            return {
                Nome: produto.nome,
                "Código de Barras": produto.codigo_barras,
                "Preço Custo": lote.preco_custo ? `R$ ${Number(lote.preco_custo).toFixed(2)}` : "-",
                Peso: produto.peso_embalagem,
                Unidade: produto.unidade_peso,
                "Estoque Mínimo": produto.estoque_minimo,
                "Estoque Atual": produto.estoque_atual
            };
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inventário");
        XLSX.writeFile(workbook, `Inventario_${dataHora}.xlsx`);
    };

    const exportarParaPDF = () => {
        const doc = new jsPDF();
        const dataHora = obterDataHoraAtual();

        doc.text("Relatório de Inventário", 14, 10);
        doc.text(`Exportado em: ${dataHora.replace(/_/g, ':')}`, 14, 18);

        autoTable(doc, {
            head: [["Nome", "Código de Barras", "Preço Custo", "Peso", "Unidade", "Estoque Mínimo", "Estoque Atual"]],
            body: produtos.map(produto => {
                const lote = lotes[produto.id] || {};
                return [
                    produto.nome,
                    produto.codigo_barras,
                    lote.preco_custo ? `R$ ${Number(lote.preco_custo).toFixed(2)}` : "-",
                    produto.peso_embalagem,
                    produto.unidade_peso,
                    produto.estoque_minimo,
                    produto.estoque_atual
                ];
            }),
            startY: 25,
        });

        doc.save(`Inventario_${dataHora}.pdf`);
    };

    const handleExcluirProduto = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este produto?")) {
            try {
                await api.delete(`/produtos/${id}`);
                toast.success('Produto excluído com sucesso!');
                carregarProdutos();
                carregarLotes();
            } catch (error) {
                console.log('ERRO COMPLETO:', error);
                console.log('ERRO RESPONSE:', error.response);
                console.log('ERRO DATA:', error.response?.data);
                
                if (error.response?.data?.fichas) {
                    console.log('ENTROU NO IF DAS FICHAS');
                    toast.error(
                        <div>
                            <strong>{error.response.data.mensagem}</strong>
                            <div style={{ marginTop: '10px', fontSize: '14px' }}>
                                {error.response.data.fichas.map((ficha, idx) => (
                                    <div key={idx}>{ficha}</div>
                                ))}
                            </div>
                        </div>,
                        {
                            autoClose: 8000,
                            style: { width: '400px' }
                        }
                    );
                } else {
                    console.log('NÃO ENTROU NO IF DAS FICHAS');
                    toast.error(error.response?.data?.erro || 'Erro ao excluir produto');
                }
            }
        }
    };

    const handleEditarProduto = (produto) => {
        setProdutoEditado(produto);
        setMostrarModalEdicao(true);
    };

    const fecharModalEdicao = () => {
        setProdutoEditado(null);
        setMostrarModalEdicao(false);
        carregarProdutos();
        carregarLotes();
    };

    // Componente Card para Mobile
    const ProductCard = ({ produto }) => {
        const lote = lotes[produto.id] || {};
        const isStockCritical = produto.estoque_atual <= 0;
        const isStockLow = produto.estoque_atual > 0 && produto.estoque_atual <= produto.estoque_minimo;

        return (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 text-base truncate">{produto.nome}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500">{produto.codigo_barras}</span>
                            {lote?.preco_custo && (
                                <span className="text-xs font-medium text-green-600">
                                    R$ {lote.preco_custo.toFixed(2)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div>
                        <span className="text-gray-500">Peso:</span>
                        <span className="ml-1 text-gray-700">{produto.peso_embalagem} {produto.unidade_peso}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Est. Mín:</span>
                        <span className="ml-1 text-gray-700">{produto.estoque_minimo}</span>
                    </div>
                    <div className="col-span-2">
                        <span className="text-gray-500">Estoque Atual:</span>
                        <span className={`ml-1 font-semibold ${
                            isStockCritical ? 'text-red-600' : 
                            isStockLow ? 'text-yellow-600' : 
                            'text-green-600'
                        }`}>
                            {produto.estoque_atual}
                        </span>
                        {isStockCritical && <span className="ml-2 text-red-500 text-xs">Esgotado</span>}
                        {isStockLow && <span className="ml-2 text-yellow-500 text-xs">Baixo</span>}
                    </div>
                </div>

                <div className="flex gap-1">
                    <button 
                        className="flex-1 bg-green-500 text-white p-2 rounded text-xs hover:bg-green-600"
                        onClick={() => {
                            setProdutoMovimentacao(produto);
                            setTipoMovimentacao('entrada');
                        }}
                    >
                        <FaPlus className="inline" /> Entrada
                    </button>
                    <button 
                        className="flex-1 bg-yellow-500 text-white p-2 rounded text-xs hover:bg-yellow-600"
                        onClick={() => {
                            setProdutoMovimentacao(produto);
                            setTipoMovimentacao('saida');
                        }}
                    >
                        <FaMinus className="inline" /> Saída
                    </button>
                    <button 
                        className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                        onClick={() => handleEditarProduto(produto)}
                    >
                        <FaEdit size={14} />
                    </button>
                    <button 
                        className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                        onClick={() => handleExcluirProduto(produto.id)}
                    >
                        <FaTrash size={14} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="max-w-full md:max-w-6xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-4 md:mb-6">
                    Relatório de Produtos
                </h2>

                {/* Toast de Estoque Crítico - Moderno e Discreto */}
                {estoqueCritico > 0 && exibirAlerta && (
                    <div className="fixed bottom-4 left-4 right-4 md:right-auto md:bottom-6 md:left-6 max-w-sm bg-white/80 backdrop-blur-md border border-orange-200/50 p-4 rounded-2xl shadow-lg z-50 animate-slide-in">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
                                    <FaInfoCircle className="text-white text-base" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1.5">
                                    <h4 className="text-sm font-semibold text-gray-900">
                                        Estoque Baixo
                                    </h4>
                                    <button
                                        onClick={() => setExibirAlerta(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                                    >
                                        <FaTimes className="text-xs" />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-600 mb-3">
                                    {estoqueCritico} {estoqueCritico === 1 ? 'produto precisa' : 'produtos precisam'} de atenção
                                </p>
                                <Link
                                    to="/alertas"
                                    onClick={() => setExibirAlerta(false)}
                                    className="no-underline inline-flex items-center gap-1.5 text-xs font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 px-4 py-2 rounded-lg transition-all shadow-sm hover:shadow-md"
                                >
                                    Ver detalhes
                                    <span className="text-xs">→</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {/* Busca */}
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Filtrar por nome do produto..."
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        value={filtroProduto}
                        onChange={(e) => setFiltroProduto(e.target.value)}
                    />
                </div>

                {/* Filtros e Toggle */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <button
                        onClick={() => setFiltrarCriticos(!filtrarCriticos)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                            filtrarCriticos 
                                ? 'bg-red-500 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaInfoCircle className={filtrarCriticos ? 'text-yellow-300' : ''} />
                        <span className="hidden sm:inline">
                            {filtrarCriticos ? 'Mostrando apenas críticos' : 'Filtrar estoque crítico'}
                        </span>
                        <span className="sm:hidden">Críticos</span>
                        {filtrarCriticos && produtosFiltrados.length > 0 && (
                            <span className="bg-yellow-400 text-red-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                                {produtosFiltrados.length}
                            </span>
                        )}
                    </button>

                    {/* Toggle de Visualização (apenas desktop) */}
                    {!isMobile && (
                        <>
                            <button
                                onClick={() => setViewMode('table')}
                                className={`px-4 py-2 rounded-lg ${
                                    viewMode === 'table' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-gray-200 text-gray-700'
                                }`}
                            >
                                Tabela
                            </button>
                            <button
                                onClick={() => setViewMode('cards')}
                                className={`px-4 py-2 rounded-lg ${
                                    viewMode === 'cards' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-gray-200 text-gray-700'
                                }`}
                            >
                                Cards
                            </button>
                        </>
                    )}
                </div>

                {/* Ações */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
                    <div className="flex flex-wrap gap-2 w-full md:w-auto">
                        <button 
                            className="flex-1 md:flex-none bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2" 
                            onClick={exportarParaExcel}
                        >
                            <FaFileExcel />
                            <span className="hidden sm:inline">Exportar Excel</span>
                            <span className="sm:hidden">Excel</span>
                        </button>
                        <button 
                            className="flex-1 md:flex-none bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2" 
                            onClick={exportarParaPDF}
                        >
                            <FaFilePdf />
                            <span className="hidden sm:inline">Exportar PDF</span>
                            <span className="sm:hidden">PDF</span>
                        </button>
                    </div>
                    <button
                        className="w-full md:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                        onClick={() => {
                            setProdutoEditado(null);
                            setMostrarModalEdicao(true);
                        }}
                    >
                        <FaPlus />
                        Adicionar Produto
                    </button>
                </div>

                {/* Contador de Resultados */}
                {filtrarCriticos && produtosFiltrados.length > 0 && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 font-medium text-sm">
                            {produtosFiltrados.length} produto{produtosFiltrados.length > 1 ? 's' : ''} com estoque crítico encontrado{produtosFiltrados.length > 1 ? 's' : ''}
                        </p>
                    </div>
                )}

                {filtroProduto && !filtrarCriticos && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-700 font-medium text-sm">
                            {produtosFiltrados.length} produto{produtosFiltrados.length !== 1 ? 's' : ''} encontrado{produtosFiltrados.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                )}

                {/* Visualização Condicional */}
                {viewMode === 'table' ? (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Nome</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Código</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Preço Custo</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Peso</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Unidade</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Est. Mín</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Estoque</th>
                                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {produtosFiltrados.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="border border-gray-300 p-6 text-center text-gray-500">
                                            {filtrarCriticos 
                                                ? "Nenhum produto com estoque crítico encontrado" 
                                                : filtroProduto
                                                ? "Nenhum produto encontrado"
                                                : "Nenhum produto cadastrado. Clique em 'Adicionar Produto'"
                                            }
                                        </td>
                                    </tr>
                                ) : (
                                    produtosFiltrados.map((produto) => {
                                        const lote = lotes[produto.id] || {};
                                        const isStockCritical = produto.estoque_atual <= 0;
                                        const isStockLow = produto.estoque_atual > 0 && produto.estoque_atual <= produto.estoque_minimo;
                                        
                                        return (
                                            <tr key={produto.id} className="border-gray-300 hover:bg-gray-50 transition-colors">
                                                <td className="border border-gray-300 px-3 py-1.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">
                                                    {produto.nome}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-1.5 text-center text-gray-600">
                                                    {produto.codigo_barras}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-1.5 text-center text-gray-700">
                                                    {lote?.preco_custo ? `R$ ${lote.preco_custo.toFixed(2)}` : "-"}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-1.5 text-center text-gray-600">
                                                    {produto.peso_embalagem}
                                                </td>
                                                <td className="border border-gray-300 px-3 py-1.5 text-center">
                                                    <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                                        {produto.unidade_peso}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-300 px-3 py-1.5 text-center text-gray-600">
                                                    {produto.estoque_minimo}
                                                </td>
                                                <td className={`border border-gray-300 px-3 py-1.5 text-center font-semibold ${
                                                    isStockCritical ? 'text-red-600' : 
                                                    isStockLow ? 'text-yellow-600' : 
                                                    'text-green-600'
                                                }`}>
                                                    {produto.estoque_atual}
                                                </td>
                                                <td className="border border-gray-300 px-2 py-1">
                                                    <div className="flex gap-1 items-center justify-center">
                                                        <button 
                                                            className="bg-green-500 text-white p-1.5 rounded hover:bg-green-600 transition-colors" 
                                                            onClick={() => {
                                                                setProdutoMovimentacao(produto);
                                                                setTipoMovimentacao('entrada');
                                                            }}
                                                            title="Entrada de estoque"
                                                        >
                                                            <FaPlus size={12} /> 
                                                        </button>
                                                        <button 
                                                            className="bg-yellow-500 text-white p-1.5 rounded hover:bg-yellow-600 transition-colors" 
                                                            onClick={() => {
                                                                setProdutoMovimentacao(produto);
                                                                setTipoMovimentacao('saida');
                                                            }}
                                                            title="Saída de estoque"
                                                        >
                                                            <FaMinus size={12} /> 
                                                        </button>
                                                        <button 
                                                            className="bg-gray-500 text-white p-1.5 rounded hover:bg-gray-600 transition-colors" 
                                                            onClick={() => handleEditarProduto(produto)}
                                                            title="Editar produto"
                                                        >
                                                            <FaEdit size={12} />
                                                        </button>
                                                        <button 
                                                            className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors" 
                                                            onClick={() => handleExcluirProduto(produto.id)}
                                                            title="Excluir produto"
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {produtosFiltrados.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                {filtrarCriticos 
                                    ? "Nenhum produto com estoque crítico encontrado" 
                                    : filtroProduto
                                    ? "Nenhum produto encontrado"
                                    : "Nenhum produto cadastrado"
                                }
                            </div>
                        ) : (
                            produtosFiltrados.map((produto) => (
                                <ProductCard key={produto.id} produto={produto} />
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Modais */}
            {mostrarModalEdicao && (
                <ProductForm
                    produtoEditado={produtoEditado}
                    fecharModal={fecharModalEdicao}
                />
            )}

             {/* Avisos */}
                <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                />

            {tipoMovimentacao === 'entrada' && (
                <StockEntry
                    produtoSelecionado={produtoMovimentacao}
                    isModal={true}
                    fecharModal={() => {    
                        setProdutoMovimentacao(null);
                        setTipoMovimentacao(null);
                        carregarProdutos();
                        carregarLotes();
                    }}
                />
            )}
            
            {tipoMovimentacao === 'saida' && (
                <StockExit
                    produtoSelecionado={produtoMovimentacao}
                    isModal={true}
                    fecharModal={() => {
                        setProdutoMovimentacao(null);
                        setTipoMovimentacao(null);
                        carregarProdutos();
                        carregarLotes();
                    }}
                />
            )}
        </>
    );
};

export default InventoryList;